<template>
  <div>
    <div class="relative z-[2]">
      <ULandingHero
        :title="page?.body.hero.title"
        :description="page?.body.hero.description"
        :links="page?.body.hero.links"
        class="bg-transparent dark:bg-transparent !py-40 relative"
      >
        <template #headline>
          <UBadge
            v-if="page?.body.hero.headline"
            variant="subtle"
            size="lg"
            class="relative rounded-full font-semibold"
          >
            <NuxtLink
              :to="page?.body.hero.headline.to"
              target="_blank"
              class="focus:outline-none"
              tabindex="-1"
            >
              <span class="absolute inset-0" aria-hidden="true" />
            </NuxtLink>

            {{ page?.body.hero.headline.label }}

            <UIcon
              v-if="page?.body.hero.headline.icon"
              :name="page?.body.hero.headline.icon"
              class="ml-1 w-4 h-4 pointer-events-none"
            />
          </UBadge>
        </template>
        <template #default>
          <img
            class="z-[-1] object-fill w-[300px] lg:w-[200px] xl:w-[340px] max-w-none absolute top-1/2 -translate-y-[50%] opacity-[0.15] right-0 dark:opacity-[0.15]"
            src="~/assets/images/logomark-white.svg"
            alt=""
          />
        </template>
      </ULandingHero>
      <ULandingSection
        :title="page?.body.pricing.title"
        :description="page?.body.pricing.description"
        :headline="page?.body.pricing.headline"
        class="bg-neutral-50 dark:bg-neutral-950 !py-24 lg:px-20"
        :ui="{
          container:
            '!max-w-full mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl !gap-16 sm:!gap-y-24 flex flex-col lg:pb-24',
        }"
      >
        <UPricingGrid
          id="pricing"
          compact
          class="scroll-mt-[calc(var(--header-height)+140px+128px+96px)] flex flex-col items-center 2xl:flex-row 2xl:justify-center !gap-10 !2xl:gap-20"
        >
          <PricingCard
            v-for="(plan, index) in page?.body.pricing.plans"
            :key="index"
            v-bind="plan"
            :ui="{
              title:
                'text-2xl text-gray-900 dark:text-white sm:text-3xl font-semibold max-w-[400px]',
            }"
          />
        </UPricingGrid>
      </ULandingSection>

      <ULandingSection class="bg-neutral-100 dark:bg-transparent !py-24">
        <table class="table-fixed">
          <thead>
            <tr>
              <th class="text-left md:pr-5 md:w-2/3">
                <h2
                  class="text-4xl md:text-[62px] font-bold tracking-tight pb-8 max-w-[440px] leading-tight"
                >
                  {{ page?.body.planComparison.title }}
                </h2>
              </th>
              <th
                v-for="plan in page?.body.planComparison.plans"
                class="text-left tracking-tight align-bottom pb-8 md:text-xl md:w-[200px] md:pr-4"
              >
                <span class="block" style="text-wrap: pretty">{{ plan.title }}</span>
                <UButton
                  :to="{
                    path: '/sign-up',
                    query: {
                      membershipType: plan.membershipType,
                    },
                  }"
                  class="hidden md:inline-flex text-sm md:text-lg !bg-cardinal !text-white whitespace-nowrap mt-6"
                >
                  <i class="fas mr-2" :class="[plan.icon]" />
                  {{ plan.buttonText }}
                </UButton>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="feature in page?.body.planComparison.features"
              class="border-b border-dashed border-b-black/[0.35] dark:border-b-white/[0.35]"
            >
              <td class="py-4 pr-3 md:pr-8 flex gap-3 items-center">
                <template v-if="feature.baseballOnly">
                  <div
                    class="border border-white flex items-center p-2 whitespace-nowrap text-sm rounded"
                  >
                    Baseball Only *
                  </div>
                </template>
                <span
                  class="text-sm font-regular md:text-lg block"
                  :class="{
                    'font-bold': feature.bold,
                  }"
                  >{{ feature.label }}</span
                >
              </td>
              <td class="py-4" v-for="plan in feature.plans">
                <template v-if="plan === 1">
                  <span
                    class="flex items-center justify-center bg-cardinal-900 dark:bg-cardinal-950 w-10 h-10 md:w-12 md:h-12 rounded-full"
                  >
                    <i class="fas fa-check text-white md:text-2xl"></i>
                  </span>
                </template>
                <template v-else></template>
              </td>
            </tr>
          </tbody>
        </table>
        <p class="-mt-5 text-neutral-300">
          * Certain features are currently only available for Baseball, although these features may
          also be added for Softball in the near future. Want to see them added sooner?
          <a
            class="font-bold underline text-white"
            href="#"
            @click.prevent="
              openIntercom(
                'I would like to see the Baseball-only features apply to Softball as well.'
              )
            "
            >Let us know</a
          >
        </p>
      </ULandingSection>

      <ULandingSection class="bg-cardinal dark:bg-cardinal -mb-8">
        <ULandingCTA
          v-bind="page?.body.cta"
          :card="false"
          :ui="{
            title: 'text-3xl font-bold tracking-tight text-white sm:text-4xl',
            description: 'text-white font-medium text-xl dark:text-white',
          }"
        >
          <template #links>
            <UButton
              size="xl"
              @click.prevent="
                openIntercom('I would like more info about 64 Analytics Pricing and Plans')
              "
              class="btn btn-primary"
            >
              <i class="fas fa-headset"></i>
              Contact Support
            </UButton>
          </template>
        </ULandingCTA>
      </ULandingSection>
    </div>
  </div>
</template>

<script setup lang="ts">
const { data: page } = await useAsyncData('pricing-page', () =>
  queryCollection('landing').path('/landing/pricing').first()
);

const { openIntercom } = useIntercom();

useSeoMeta({
  title: page?.value?.title ?? '64 Analytics Pricing',
  ogTitle: page?.value?.title ?? '64 Analytics Pricing',
  description: page?.value?.description ?? '',
  ogDescription: page?.value?.description ?? '',
});
</script>
